import { useState } from "react";
import { Truck, Package, MapPin, CheckCircle, Circle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const STAGES = [
  { key: "not_shipped", label: "Not shipped", icon: Circle },
  { key: "shipped", label: "Shipped", icon: Package },
  { key: "in_transit", label: "In transit", icon: Truck },
  { key: "out_for_delivery", label: "Out for delivery", icon: MapPin },
  { key: "delivered", label: "Delivered", icon: CheckCircle },
];

const stageIndex = (stage) => STAGES.findIndex((s) => s.key === (stage || "not_shipped"));

export const ShippingBadge = ({ request }) => {
  const idx = stageIndex(request.shipping_stage);
  if (idx <= 0) return <span className="text-xs text-slate-400">—</span>;
  const { label, icon: Icon } = STAGES[idx];
  return (
    <span
      data-testid={`shipping-badge-${request.id}`}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7]"
    >
      <Icon size={14} weight="bold" /> {label}
    </span>
  );
};

export const ShippingTimeline = ({ request, testid }) => {
  const current = stageIndex(request.shipping_stage);
  const events = request.shipping_events || [];
  return (
    <div data-testid={testid} className="mt-3 border-t border-border pt-3">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        {request.carrier && (
          <span>
            Carrier <span className="font-semibold text-[#0F172A]">{request.carrier}</span>
          </span>
        )}
        {request.tracking_number && (
          <span>
            Tracking{" "}
            <span data-testid={`tracking-number-${request.id}`} className="font-mono text-[#0F172A]">
              {request.tracking_number}
            </span>
          </span>
        )}
        {request.eta && (
          <span>
            ETA <span className="font-semibold text-[#0F172A]">{request.eta}</span>
          </span>
        )}
      </div>

      <ol className="flex flex-col gap-0 sm:flex-row sm:items-start sm:gap-1">
        {STAGES.slice(1).map((s, i) => {
          const reached = current >= i + 1;
          const event = [...events].reverse().find((e) => e.stage === s.key);
          const Icon = s.icon;
          return (
            <li key={s.key} className="flex flex-1 gap-2 sm:flex-col sm:gap-1.5">
              <div className="flex flex-col items-center sm:w-full sm:flex-row">
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center border ${
                    reached
                      ? "border-[#002FA7] bg-[#002FA7] text-white"
                      : "border-slate-300 bg-white text-slate-300"
                  }`}
                >
                  <Icon size={14} weight="bold" />
                </span>
                <span
                  className={`h-6 w-px sm:h-px sm:w-full ${reached ? "bg-[#002FA7]" : "bg-slate-200"} ${
                    i === STAGES.length - 2 ? "sm:hidden" : ""
                  }`}
                />
              </div>
              <div className="pb-3 sm:pb-0">
                <div
                  data-testid={`stage-${s.key}-${request.id}`}
                  className={`text-xs font-semibold ${reached ? "text-[#0F172A]" : "text-slate-400"}`}
                >
                  {s.label}
                </div>
                {event && (
                  <div className="text-[0.7rem] leading-snug text-slate-400">
                    {(event.at || "").slice(0, 16).replace("T", " ")}
                    {event.note ? ` · ${event.note}` : ""}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export const ShippingDialog = ({ request, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({
    carrier: request?.carrier || "",
    tracking_number: request?.tracking_number || "",
    shipping_stage: request?.shipping_stage || "not_shipped",
    eta: request?.eta || "",
    note: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/requests/${request.id}/shipping`, form);
      toast.success("Shipping updated");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Update shipping</DialogTitle>
          <DialogDescription>{request?.item_name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="shipping-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="overline text-slate-500">Carrier</Label>
              <Input
                data-testid="shipping-carrier-input"
                value={form.carrier}
                onChange={(e) => setForm({ ...form, carrier: e.target.value })}
                placeholder="e.g. DHL"
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
            <div>
              <Label className="overline text-slate-500">Tracking number</Label>
              <Input
                data-testid="shipping-tracking-input"
                value={form.tracking_number}
                onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
            <div>
              <Label className="overline text-slate-500">Stage</Label>
              <Select
                value={form.shipping_stage}
                onValueChange={(v) => setForm({ ...form, shipping_stage: v })}
              >
                <SelectTrigger data-testid="shipping-stage-select" className="mt-1.5 rounded-none border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline text-slate-500">ETA</Label>
              <Input
                data-testid="shipping-eta-input"
                type="date"
                value={form.eta}
                onChange={(e) => setForm({ ...form, eta: e.target.value })}
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
          </div>
          <div>
            <Label className="overline text-slate-500">Note (optional)</Label>
            <Input
              data-testid="shipping-note-input"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. Held at depot overnight"
              className="mt-1.5 rounded-none border-slate-300"
            />
          </div>
          <DialogFooter>
            <Button
              data-testid="save-shipping-button"
              type="submit"
              disabled={busy}
              className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto"
            >
              {busy ? "Saving…" : "Save shipping"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
